// externals
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import type { FC } from 'react';
import { Route } from 'react-router';
import { StyleSheetManager as ScStyleSheetManager, ThemeProvider } from 'styled-components';

// libraries
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';
import { useAppNavigation } from '@makemydeal/dr-dash-store/hooks/useAppNavigation';
import type { InitConfigurationStaticAssetsState } from '@makemydeal/dr-dash-types';
import type { FeatureTogglesAndUrlOnly } from '@makemydeal/dr-shared-store';
import type { Dealer } from '@makemydeal/dr-shared-types';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { ScopedStateProvider } from '@makemydeal/shared-scoped-store';
import { ViewAsBanner } from '@makemydeal/dr-activities-role-assumption';
// components
import {
    CustomerUIProvider,
    HeaderInterstate,
    HeaderMeasureProvider,
    AssetsProvider,
    SlideoutDrawerProvider,
    themeHooks,
    NoDealershipAccess,
    ValidationGuardProvider,
    ConditionalProfiler,
    startProfilerTelemetry
} from '@makemydeal/dr-dash-components';

import { Dashboard } from '../components/dashboard/Dashboard';
import { DealAssistantLayout } from '../components/dealAssistant/DealAssistantLayout';
import { DealAssistantProviderGate } from '../components/dealAssistant/DealAssistantProviderGate';
import { useDealAssistantEntry } from '../components/dealAssistant/useDealAssistantEntry';
import { OpenDealContainer } from '../activities/open-deal-dashboard/components/OpenDealContainer';
import { RecentDealsLayout } from '../activities/recent-deals/components/RecentDealsLayout';

// styles
import { ModalAttachpointContainer } from './routes.style';
import './routing.css';
import { OPEN_DEAL_ROOT_ROUTE } from '../activities/open-deal-dashboard/constants';
import { RoleAssumptionContainer } from '../activities/role-assumption/components/RoleAssumptionContainer';

export type RoutesStateProps = {
    toggles: FeatureTogglesAndUrlOnly;
    staticAssets: InitConfigurationStaticAssetsState | undefined;
    dealer: Partial<Dealer>;
};

export type RoutesDispatchProps = Record<string, never>;

export type RoutesProps = RoutesStateProps & RoutesDispatchProps;

export type ThemeContent = Record<string, string>;

const StyleSheetManager = ScStyleSheetManager as any;

// Start the profiler telemetry flush scheduler (no-op if profiling build is not active)
startProfilerTelemetry();

export const Routes: FC<RoutesProps> = ({ staticAssets, toggles, dealer }) => {
    const { theme, getTheme } = themeHooks.useTheme(staticAssets);
    useAppNavigation(OPEN_DEAL_ROOT_ROUTE);
    const dealAssistantEntry = useDealAssistantEntry();

    return (
        <MuiThemeProvider muiTheme={getTheme()}>
            {/* Keep disableCSSOMInjection false as it is generally faster */}
            <StyleSheetManager disableCSSOMInjection={false}>
                <ThemeProvider theme={theme}>
                    <ModalAttachpointContainer className="dash-modal-attachpoint">
                        <CustomerUIProvider>
                            {/* SlideoutDrawerProvider is rendered outside of the routes, but, through context, it renders
                            content belonging to pages other than the dashboard.
                            So we have to wrap it into a navigationScope, so that the content it renders properly reflect the scenario that opened it.
                            Then we immediately set the primary scope back to it's children, so the dashboard, header/footer will all reflect the primary scenario.
                            */}
                            <ScopedStateProvider navigationScope>
                                <SlideoutDrawerProvider>
                                    <ScopedStateProvider primary>
                                        <HeaderMeasureProvider>
                                            <DealAssistantProviderGate>
                                                <AssetsProvider>
                                                    <ValidationGuardProvider>
                                                        <HeaderInterstate
                                                            appPrefix={MANAGER_VIEW_APP_PREFIX}
                                                            viewAsBanner={<ViewAsBanner />}
                                                            assistantEntry={dealAssistantEntry}
                                                        />
                                                        <RoleAssumptionContainer />
                                                        <RecentDealsLayout>
                                                            <DealAssistantLayout>
                                                                <ConditionalProfiler id="mv-dashboard">
                                                                    <Dashboard
                                                                        staticAssets={staticAssets}
                                                                        toggles={toggles}
                                                                        dealer={dealer}
                                                                    />
                                                                </ConditionalProfiler>
                                                            </DealAssistantLayout>
                                                        </RecentDealsLayout>
                                                        <OpenDealContainer />
                                                        {/* Todo: NoDealershipAccess route as per activity pattern need to revisit later */}
                                                        <Route path={RouteValues.NO_DEALERSHIPS} component={NoDealershipAccess} />
                                                    </ValidationGuardProvider>
                                                </AssetsProvider>
                                            </DealAssistantProviderGate>
                                        </HeaderMeasureProvider>
                                    </ScopedStateProvider>
                                </SlideoutDrawerProvider>
                            </ScopedStateProvider>
                        </CustomerUIProvider>
                    </ModalAttachpointContainer>
                </ThemeProvider>
            </StyleSheetManager>
        </MuiThemeProvider>
    );
};
